import React, { useCallback, useEffect, useState } from 'react';
import { nlt } from '../../services/api';
import type { NLTConfig } from '../../types/nlt';
import type { AgentConfiguratorState } from '../../types/agentConfigurator';
import AgentConfigurator from '../agent-configurator/AgentConfigurator';
import { capitalizeError } from '../../utils/errorUtils';
import { NOTIFICATION_AUTO_HIDE_MS } from '../../constants/uiTiming';
import { NLT_ADMIN_FINAL_CONFIG, NLT_ADMIN_FINAL_CONFIG_LABEL } from '../../constants/nltAdminFinalConfig';
import { serializeNltAdminConfigForSave } from '../../constants/nltAdminAgentTools';
import { invalidateLatestNltAdminConfigCache } from '../../utils/nltAdminConfigPayload';
import Notification from '../Notification';
import ConfirmModal from '../ConfirmModal';
import './NLTConfigPanel.css';

interface NLTConfigPanelProps {
  onOpenAdminTour?: () => void;
}

const NLTConfigPanel: React.FC<NLTConfigPanelProps> = ({ onOpenAdminTour }) => {
  const [configs, setConfigs] = useState<NLTConfig[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [editingConfig, setEditingConfig] = useState<NLTConfig | null | 'new'>('new');
  const [editingConfigLoadingId, setEditingConfigLoadingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [latestConfig, setLatestConfig] = useState<NLTConfig | null>(null);
  const [latestLoading, setLatestLoading] = useState(false);

  const [notification, setNotification] = useState({
    isVisible: false,
    type: 'success' as 'success' | 'error',
    message: '',
  });

  const [searchQ, setSearchQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; config: NLTConfig | null }>({
    isOpen: false,
    config: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const LIMIT = 20;

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ isVisible: true, type, message });
  }, []);

  const loadConfigs = useCallback(
    async (reset = false) => {
      setLoading(true);
      setListError(null);
      const newOffset = reset ? 0 : offset;
      if (reset) setOffset(0);
      try {
        const res = await nlt.listNLTConfigs({ limit: LIMIT, offset: newOffset, q: searchQ || undefined });
        if (res.success && res.data) {
          setConfigs(res.data.items);
          setTotal(res.data.pagination.total);
        } else {
          setListError(capitalizeError(res.error?.message || 'Failed to load NLT configs.'));
        }
      } catch (e: any) {
        setListError(capitalizeError(e?.response?.data?.error?.message || e?.message || 'Failed to load NLT configs.'));
      } finally {
        setLoading(false);
      }
    },
    [offset, searchQ]
  );

  const loadLatest = useCallback(async () => {
    setLatestLoading(true);
    try {
      const res = await nlt.getLatestNLTConfig();
      if (res.success && res.data) setLatestConfig(res.data);
    } catch {
      // latest may not exist yet
    } finally {
      setLatestLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs(true);
    loadLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upsertConfigInList = useCallback((saved: NLTConfig) => {
    setConfigs((prev) => {
      const index = prev.findIndex((item) => item.id === saved.id);
      if (index === -1) return [saved, ...prev];
      const next = [...prev];
      next[index] = saved;
      return next;
    });
  }, []);

  const handleEditConfig = useCallback(
    async (cfg: NLTConfig) => {
      setEditingConfig(cfg);
      setEditingConfigLoadingId(cfg.id);
      try {
        const res = await nlt.getNLTConfig(cfg.id);
        if (res.success && res.data) {
          setEditingConfig(res.data);
          upsertConfigInList(res.data);
        }
      } catch {
        // Fall back to the list row payload when detail endpoint is unavailable.
      } finally {
        setEditingConfigLoadingId(null);
      }
    },
    [upsertConfigInList]
  );

  // AgentConfigurator onSave handler
  const handleSave = async (cfg: AgentConfiguratorState, label?: string) => {
    setSaving(true);
    const name = (label?.trim()) || 'NLT Config';
    const body: { id?: string; name: string; config: Record<string, any> } = {
      name,
      config: serializeNltAdminConfigForSave(cfg),
    };
    if (editingConfig && editingConfig !== 'new') {
      body.id = editingConfig.id;
    }
    const wasNew = editingConfig === 'new';
    try {
      const res = await nlt.saveNLTConfig(body);
      if (res.success && res.data) {
        showNotification('success', wasNew ? 'NLT config created.' : 'NLT config updated.');
        invalidateLatestNltAdminConfigCache();
        setEditingConfig(res.data);
        upsertConfigInList(res.data);
        loadLatest();
        if (wasNew) {
          loadConfigs(true);
        }
      } else {
        showNotification('error', capitalizeError(res.error?.message || 'Failed to save NLT config.'));
      }
    } catch (e: any) {
      showNotification('error', capitalizeError(e?.response?.data?.error?.message || e?.message || 'Failed to save NLT config.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = (cfg: NLTConfig) => {
    setDeleteConfirm({ isOpen: true, config: cfg });
  };

  const confirmDeleteConfig = async () => {
    const cfg = deleteConfirm.config;
    if (!cfg?.id) return;
    setDeleteLoading(true);
    try {
      const res = await nlt.deleteNLTConfig(cfg.id);
      if (res.success === false) {
        showNotification('error', capitalizeError(res.error?.message || 'Failed to delete NLT config.'));
        return;
      }
      setDeleteConfirm({ isOpen: false, config: null });
      if (editingConfig && editingConfig !== 'new' && editingConfig.id === cfg.id) {
        setEditingConfig('new');
      }
      showNotification('success', `NLT config "${cfg.name}" deleted.`);
      invalidateLatestNltAdminConfigCache();
      loadConfigs(true);
      loadLatest();
    } catch (e: any) {
      showNotification(
        'error',
        capitalizeError(e?.response?.data?.error?.message || e?.message || 'Failed to delete NLT config.')
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const editingInitialConfig: AgentConfiguratorState | null =
    editingConfig && editingConfig !== 'new'
      ? (editingConfig.config as AgentConfiguratorState)
      : NLT_ADMIN_FINAL_CONFIG;
  const editingInitialLabel: string =
    editingConfig && editingConfig !== 'new' ? editingConfig.name : NLT_ADMIN_FINAL_CONFIG_LABEL;

  return (
    <div className="admin-section nlt-config-panel" id="admin-nlt-config-section">
      <div className="admin-section-header" id="admin-nlt-config-header">
        <h2>NLT Configs ({total})</h2>
        <div className="nlt-config-panel__toolbar">
          <div className="nlt-config-panel__search">
            <input
              type="text"
              placeholder="Search by name"
              className="filter-input"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadConfigs(true); }}
            />
            <button type="button" onClick={() => loadConfigs(true)} className="action-btn apply-btn">
              <i className="fas fa-search" aria-hidden="true"></i>
              <span className="btn-text">Search</span>
            </button>
            <button type="button" onClick={() => loadConfigs(true)} className="action-btn refresh-btn">
              <i className="fas fa-sync-alt" aria-hidden="true"></i>
              <span className="btn-text">Refresh</span>
            </button>
          </div>
          <div className="nlt-config-panel__actions">
            {onOpenAdminTour && (
              <button
                type="button"
                onClick={onOpenAdminTour}
                className="action-btn nlt-config-panel__btn-help"
              >
                <i className="fas fa-question-circle" aria-hidden="true"></i>
                <span className="btn-text">Help</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditingConfig('new')}
              className="action-btn nlt-config-panel__btn-new"
            >
              <i className="fas fa-plus" aria-hidden="true"></i>
              <span className="btn-text">New Config</span>
            </button>
          </div>
        </div>
      </div>

      {/* Latest config badge */}
      {!latestLoading && latestConfig && (
        <div className="connector-message info" style={{ marginBottom: 16 }}>
          <div className="message-content">
            <i className="fas fa-star"></i>
            <span>
              <strong>Latest active config:</strong> {latestConfig.name}{' '}
              <span style={{ color: '#7F9AB3', fontSize: 12 }}>({latestConfig.id})</span>
            </span>
          </div>
        </div>
      )}

      {/* Configs list */}
      <div id="admin-nlt-config-list" className="nlt-config-panel__list">
      {listError && (
        <div className="admin-message error" style={{ marginBottom: 12 }}>
          <i className="fas fa-exclamation-circle"></i> {listError}
        </div>
      )}

      {loading ? (
        <div className="admin-loading">
          <div className="spinner"></div>
          <p>Loading NLT configs…</p>
        </div>
      ) : configs.length === 0 ? (
        <div className="admin-message info" style={{ marginBottom: 16 }}>
          <div className="message-content">
            <i className="fas fa-info-circle"></i>
            <span>No NLT configs yet. Use the configurator below to create one.</span>
          </div>
        </div>
      ) : (
        <div className="admin-table-wrapper" style={{ marginBottom: 24 }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Updated At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((cfg) => (
                <tr
                  key={cfg.id}
                  style={
                    editingConfig && editingConfig !== 'new' && editingConfig.id === cfg.id
                      ? { background: 'var(--highlight, #F5F9FE)' }
                      : undefined
                  }
                >
                  <td>
                    {cfg.name}
                    {latestConfig?.id === cfg.id && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 11,
                          background: '#0099FA',
                          color: '#FFFFFF',
                          borderRadius: 4,
                          padding: '2px 6px',
                        }}
                      >
                        latest
                      </span>
                    )}
                  </td>
                  <td style={{ fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>{cfg.id}</td>
                  <td style={{ fontSize: 12 }}>{new Date(cfg.updated_at).toLocaleString()}</td>
                  <td data-label="Actions" className="actions-cell">
                    <div className="actions-wrap">
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={() => void handleEditConfig(cfg)}
                        disabled={editingConfigLoadingId === cfg.id}
                      >
                        <i
                          className={`fas ${editingConfigLoadingId === cfg.id ? 'fa-spinner fa-spin' : 'fa-edit'}`}
                          aria-hidden="true"
                        ></i>{' '}
                        {editingConfigLoadingId === cfg.id ? 'Loading…' : 'Edit'}
                      </button>
                      <button
                        type="button"
                        className="edit-btn delete-btn"
                        onClick={() => handleDeleteRequest(cfg)}
                        disabled={deleteLoading}
                      >
                        <i className="fas fa-trash" aria-hidden="true"></i> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, alignItems: 'center' }}>
          <button
            type="button"
            className="action-btn"
            disabled={offset === 0}
            onClick={() => { setOffset(Math.max(0, offset - LIMIT)); loadConfigs(); }}
          >
            <i className="fas fa-chevron-left" aria-hidden="true"></i> Prev
          </button>
          <span style={{ fontSize: 13 }}>
            {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <button
            type="button"
            className="action-btn"
            disabled={offset + LIMIT >= total}
            onClick={() => { setOffset(offset + LIMIT); loadConfigs(); }}
          >
            Next <i className="fas fa-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      )}

      {/* AgentConfigurator for create / edit */}
      <div className="nlt-config-panel__editor" id="admin-nlt-config-editor">
        <h3 className="nlt-config-panel__editor-title">
          {editingConfig === 'new' ? 'New NLT Config' : `Editing: ${(editingConfig as NLTConfig).name}`}
        </h3>
        <AgentConfigurator
          key={editingConfig === 'new' ? 'new' : (editingConfig as NLTConfig).id}
          hideApproach
          hideReminderPromptPosition
          nltAdminMode
          initialConfig={editingInitialConfig}
          initialLabel={editingInitialLabel}
          onSave={handleSave}
          onCancel={editingConfig !== 'new' ? () => setEditingConfig('new') : undefined}
        />
        {saving && (
          <div style={{ marginTop: 8, fontSize: 13, color: '#0099FA' }}>
            <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Saving…
          </div>
        )}
      </div>

      <Notification
        type={notification.type}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={() => setNotification((p) => ({ ...p, isVisible: false }))}
        duration={NOTIFICATION_AUTO_HIDE_MS}
        position="top-right"
      />

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Delete NLT Config"
        message={
          deleteConfirm.config ? (
            <>
              Delete NLT config <strong>{deleteConfirm.config.name}</strong>? This cannot be undone.
            </>
          ) : (
            'Delete this NLT config? This cannot be undone.'
          )
        }
        confirmText="Delete"
        variant="danger"
        isConfirmLoading={deleteLoading}
        onConfirm={() => void confirmDeleteConfig()}
        onCancel={() => {
          if (deleteLoading) return;
          setDeleteConfirm({ isOpen: false, config: null });
        }}
      />
    </div>
  );
};

export default NLTConfigPanel;
